"use client";

import React, { Ref, forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { FieldValues, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { api, apiVanilla, api as client } from "@/lib/trpc/client";
import type { RouterOutputs } from "@ept/api";
import SectionContainer from "./SectionContainer";
import FormMetadata from "./FormMetadata";
import SectionElements from "./SectionElements";
import { Button } from "../ui/button";
import { useActions } from "./actions";
import { EptForm } from "../form-builder/utils/types";
import { getFormValidators } from "./utils/element-validators";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "../ui/form";
import { Element } from "./types";
import { useReCaptcha } from "next-recaptcha-v3";
import FormProgressBar from "./FormProgressBar";
import { replaceMarkers } from "@/utils/genericUtils";
import DocumentVerifier from "@/app/[locale]/(doc)/components/DocumentVerifier";
import { translations } from "@/messages";
import { useLocale } from "use-intl";
import _ from "lodash";
import { getFormDefaultValues } from "./utils/default-values";
import { Card } from "../ui/card";
type FormViewerProps = {
  formData: RouterOutputs["formManagementRouter"]["getFormDataBySlug"];
  submittedForm?: {
    submittedForm: RouterOutputs["submittedFormsRouter"]["getSubmittedFormByIdentifier"]["submittedForm"];
    attachmentMap?: RouterOutputs["submittedFormsRouter"]["getSubmittedFormByIdentifier"]["attachmentMap"];
  };
  disabled?: boolean;
  canSubmit?: boolean;
  replaceMarkersData?: Record<string, string | undefined>;
  onSubmit?: (requestData: any) => Promise<void>;
  institutionSlug?: string;
  showProgressBar?: boolean;
  showSubmitButton?: boolean;
  showMetadata?: boolean;
  formDefaultValues?: Record<string, any>;
  keepFormLockedAfterSubmit?: boolean;
};
export type FormViewerRef = {
  submit: () => Promise<void>;
};

// eslint-disable-next-line react/display-name
const FormViewer = forwardRef(({
  formData,
  disabled = false,
  canSubmit = true,
  submittedForm,
  onSubmit,
  replaceMarkersData,
  showSubmitButton = true,
  institutionSlug,
  showProgressBar = true,
  showMetadata = true,
  formDefaultValues,
  keepFormLockedAfterSubmit
}: FormViewerProps, ref: Ref<FormViewerRef>) => {
  useImperativeHandle(ref, () => ({
    submit: () => {
      return formMethods.handleSubmit(handleFormSubmit, console.error)();
    }
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const locale = useLocale() as keyof typeof translations;
  const submitFormMutation = client.formsPublicRouter.submitForm.useMutation();
  const isUniquePersonalCodeQuery = async (personalCode: string) => {
    return await apiVanilla.formsPublicRouter.checkPersonalCodeUnique.query({
      personalCode
    });
  };
  const actions = useActions(formData);
  const formValidator = useMemo(() => getFormValidators(formData), [formData]);
  const formSchema = formValidator.schema;
  const defaultValues = submittedForm ? getFormDefaultValues(submittedForm) : undefined;
  const formMethods = useForm({
    resolver: zodResolver(formValidator.resolver),
    mode: "onBlur",
    defaultValues: defaultValues || formDefaultValues
  });
  const {
    executeRecaptcha
  } = useReCaptcha();
  const defaultSubmitHandler = async (requestData: any) => {
    if (!requestData || !requestData.fields.length) {
      return;
    }
    const isDev = process.env.NODE_ENV === "development";
    const token = !isDev ? await executeRecaptcha("form_submit") : "dev_token";
    submitFormMutation.mutate({
      ...requestData,
      institutionSlug,
      gRecaptchaToken: token
    });
  };
  const handleFormSubmit: SubmitHandler<FieldValues> = async () => {
    try {
      const data = formMethods.getValues();
      setIsSubmitting(true);
      const requestData = await actions?.formatElementsForRequest(data, locale);
      const submitResponse = await (onSubmit?.(requestData) ?? defaultSubmitHandler(requestData));
      setIsSubmitting(false);
      return submitResponse;
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  };
  const formHash = submitFormMutation.data?.formHash;
  const formId = submitFormMutation.data?.formId;
  const averagteTimeQuery = api.institutionProceduresRouter.getFormAverageTime.useQuery({
    institutionSlug: institutionSlug || "",
    formId: formId || ""
  }, {
    enabled: !!institutionSlug && !!formId
  });
  const countdownTo = useMemo(() => {
    if (averagteTimeQuery.data) {
      return new Date(Date.now() + averagteTimeQuery.data);
    }
  }, [averagteTimeQuery.data]);
  if (submitFormMutation.isSuccess && !keepFormLockedAfterSubmit) {
    return <Card className="flex h-full w-full flex-col flex-wrap items-center justify-center gap-10 py-10">
          <h2 className="text-4xl">{translations[locale]["formView"]["successfullySubmitted"]}</h2>
          <DocumentVerifier isValid hideScan onDocumentView={() => {
        if (formHash) {
          window.open(`/${locale}/doc/hash/${formHash}?view=true`, "_blank");
        } else {
          window.open(`/${locale}/doc/id/${formId}?view=true`, "_blank");
        }
      }} countdownTo={countdownTo} />
        </Card>;
  }
  if (!formData) {
    return null;
  }
  const isReadOnly = disabled || submitFormMutation.isSuccess && keepFormLockedAfterSubmit;
  return <FormProvider {...formMethods}>
        <Form {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(handleFormSubmit, console.error)} className="w-full">
            <div className="flex flex-col gap-4">
              {submitFormMutation.isSuccess && keepFormLockedAfterSubmit && <div className="text-center">
                  <h2 className="text-4xl">
                    {translations[locale]["formView"]["successfullySubmitted"]}
                  </h2>
                </div>}
              {showMetadata && <FormMetadata title={formData?.displayName || "Formular"} />}
              <FormProgressBar formSchema={formSchema} formData={formData} hidden={!showProgressBar} />

              {formData?.sections.map(section => <SectionContainer formData={formData} sectionId={section.sectionId!} key={section.sectionId} title={replaceMarkers(section.section.displayName || "", {
            institution: replaceMarkersData?.institution
          })}>
                  <SectionElements disabled={isReadOnly} elements={section.section.elements as Element[]} replaceMarkersData={replaceMarkersData} institutionSlug={institutionSlug} />
                </SectionContainer>)}
              {showSubmitButton && !(submitFormMutation.isSuccess && keepFormLockedAfterSubmit) ? <Button disabled={!canSubmit || isSubmitting} type="submit" className="h-12 bg-blue-700 px-6 text-white hover:bg-blue-800">
                  {isSubmitting ? translations[locale]["formView"]["isSubmitting"] : translations[locale]["institution"]["submit"]}
                </Button> : null}
            </div>
          </form>
        </Form>
      </FormProvider>;
});
export default FormViewer;